import * as logger from 'loglevel';
import { getUuidMin } from '@front10/helpers/dist/uuid';
import { getDateRangeAsDates } from '../../helpers/localization/localizationUtils';

import axiosWrapper from '../../helpers/axiosWrapper';
import { NO_DURATION_DEFINED } from '../../helpers/axiosWrapper/cache';
import transformRequestGroupRoutes from './transformRequest/transformSmartFilter';

const AGGREGATION_TYPE = {
  ROUTE_HOTEL_PACKAGE: 'ROUTE_HOTEL_PACKAGE', // -> flight outbound city, country
  HOTEL_ROUTE_PACKAGE: 'HOTEL_ROUTE_PACKAGE', // -> hotel
  ROUTE_CAR_PACKAGE: 'ROUTE_CAR_PACKAGE' // ->  car
};

/**
 * Makes sure the fares response is complain with a basic schema
 * @param {*} fares
 */
const validateFaresResponse = (fares) => {
  const validation = { isValid: true, message: '' };
  if (!fares || !Array.isArray(fares)) {
    validation.isValid = false;
    return {
      isValid: false,
      data: fares,
      message: 'INTEGRATION__FARES: Fares must be an Array'
    };
  }
  if (fares.length > 0) {
    const fareItem = fares[0];
    if (!fareItem.hotel || !fareItem.journey || !fareItem.priceSpecification) {
      return {
        isValid: false,
        data: fareItem,
        message:
          'INTEGRATION__FARES: FareItem SCHEMA is invalid, please check Fares API documentation.'
      };
    }
  }
  return validation;
};

const getCorrectImage = (
  itemRaw,
  journeyRaw,
  aggregationType = AGGREGATION_TYPE.ROUTE_CAR_PACKAGE
) => {
  const getImageFromFlight = (journeyRaw) => {
    if (Boolean(journeyRaw.outboundFlight.destination?.city?.image)) {
      return journeyRaw.outboundFlight.destination?.city?.image;
    }
    if (Boolean(journeyRaw.outboundFlight.destination?.country?.image)) {
      return journeyRaw.outboundFlight.destination?.country?.image;
    }
  };

  switch (aggregationType) {
    case AGGREGATION_TYPE.HOTEL_ROUTE_PACKAGE:
    case AGGREGATION_TYPE.ROUTE_CAR_PACKAGE:
      /*
       * From the FlightCarsPackagesGrid and FlightsCarPackagesCarousel components,
       * ROUTE_CAR_PACKAGE will always be passed (it has nothing to do with what is sent from the API, it is only to transform the image)
       */
      if (itemRaw.images?.length > 0 && itemRaw.images[0]) {
        if (itemRaw.images[0].includes('https://') || itemRaw.images[0].includes('http://')) {
          return itemRaw.images[0];
        } else {
          return itemRaw.images[0].replace('//', 'https://');
        }
      } else {
        return getImageFromFlight(journeyRaw);
      }
    //In any other case, the photo of the trip is sent
    default:
      return getImageFromFlight(journeyRaw);
  }
};


/**
 * The group-routes API does not returns the journeyType
 * so this map allows us to use query sent parameters to do it
 */
const journeyTypeMap = {
  ROUND_TRIP: 'rt',
  ONE_WAY: 'ow',
  MULTI_CITY: 'mc',
  EACH_WAY: 'ew'
};


/**
 * The group routes API returns the data in non linear format
 * so this function will intend normalize the data for the components
 * @param {*} routesRawData
 * @param {*} aggregationType Type of the aggregation used in the query.
 * @returns
 */
const transformData = (routesRawData, aggregationType) => {
  const fares = routesRawData
    .map(
      ({
        hotel: hotelRaw,
        journey: journeyRaw,
        priceSpecification: priceSpecificationRaw,
        searchDate
      }) => {
        // Workaround for Safari and IE
        if (searchDate && /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d+[+|-]\d{4}/.test(searchDate)) {
          searchDate = `${searchDate.substring(0, searchDate.length - 2)}:${searchDate.substring(
            searchDate.length - 2
          )}`;
        }

        let fields = journeyRaw.additionalInfo?.fields;
        const originDestination = Boolean(fields)
          ? fields.filter((field) => field.name === 'origin' || field.name === 'destination')
          : [];

        //PENDING: Las prioridades de las imágenes.
        const newFare = {
          hotelId: hotelRaw.id,
          hotelGroup: hotelRaw.group,
          hotelName: hotelRaw.name,
          hotelRoomCount: hotelRaw.roomCount,
          rating: hotelRaw?.rating?.value,
          ...journeyRaw,
          origin: journeyRaw.outboundFlight.departureAirportIataCode,
          aditionalOrigin: originDestination.length > 0 ? originDestination[0].value : '',
          originCity: journeyRaw.outboundFlight.origin?.city?.name,
          destination: journeyRaw.outboundFlight.arrivalAirportIataCode,
          aditionalDestination: originDestination.length > 1 ? originDestination[1].value : '',
          destinationCity: journeyRaw.outboundFlight.destination?.city?.name,
          farenetTravelClass: journeyRaw.outboundFlight.fareClass,
          ...priceSpecificationRaw,
          journeyType: journeyTypeMap[journeyRaw.journeyType],
          searchDate,
          destinationImage: getCorrectImage(hotelRaw, journeyRaw, aggregationType)
        };

        newFare.index = getUuidMin(
          `${newFare.hotelId}${newFare.hotelGroup}
            ${newFare.hotelName}
            ${newFare.hotelRoomCount}
            ${newFare.rating}
            ${newFare.origin}
            ${newFare.destination}
            ${newFare.farenetTravelClass}
            ${searchDate}`
        );

        delete newFare.outboundFlight;
        return newFare;
      }
    )
    .reduce((a, b) => a.concat(b), []); // arr.flat()
  return fares;
};

/**
 * Retrieve fares from the GroupRoutes Endpoint
 * @param {*} filter
 * @param {*} integrationSettings
 */
const getFares = (filter, integrationSettings, withAgreggations, meta, pageContext) => {
  return new Promise((resolve, reject) => {
    if (!integrationSettings) {
      const MESSAGE = `INTEGRATION__FARES: **integrationSettings** not especified.`;
      logger.error(MESSAGE);
      reject(MESSAGE);
    }
    const {
      v1Globetrotter: faresApiSettings,
      v1Globetrotter: { cache = false, cacheDuration = NO_DURATION_DEFINED }
    } = integrationSettings;
    const filterCopy = { ...filter };

    if (
      !faresApiSettings ||
      !faresApiSettings.method ||
      !faresApiSettings.endPoint ||
      !faresApiSettings.headers
    ) {
      const MESSAGE = `INTEGRATION__FARES: **integrationSettings.v1Globetrotter** configuration issues.`;
      logger.error(MESSAGE);
      reject(MESSAGE);
    }

    filterCopy.departure = getDateRangeAsDates(
      filterCopy.departure,
      'YYYY-MM-DD',
      'INTEGRATION__FARES'
    );
    if (filterCopy.return) {
      filterCopy.return = getDateRangeAsDates(
        filterCopy.return,
        'YYYY-MM-DD',
        'INTEGRATION__FARES'
      );
    }

    // params to delete don't matter the value store
    const paramsDelete = [
      'languageCode',
      'uiDatesYearsAdjustmentPatch',
      'airlineIataCode',
      'siteEdition',
      'faresLimit',
      'page',
      'airport',
      'fullyVaccinated',
      'travelRestrictionsSource',
      'affiliateId',
      'queryFields',
      'include',
      'languagesBySiteEdition'
    ];

    axiosWrapper(
      {
        method: faresApiSettings.method,
        url: `${faresApiSettings.endPoint}`,
        headers: { ...faresApiSettings.headers },
        data: transformRequestGroupRoutes(filterCopy, paramsDelete)
      },
      {
        resource: 'INTEGRATION__FARES__GLOBETROTTER',
        meta,
        integrationSettings,
        pageContext,
        cache,
        cacheDuration
      }
    )
      .then((response) => {
        const validation = validateFaresResponse(response.data);
        if (validation.isValid) {
          logger.info('INTEGRATION__FARES: Successful request', response.data);
          const fares = transformData(
            response.data,
            filterCopy?.aggregationType ?? AGGREGATION_TYPE.ROUTE_HOTEL_PACKAGE
          );
          resolve(fares);
        } else {
          logger.error(validation.message, validation.data);
          reject(response.data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export { getFares, transformData, getCorrectImage };
